/**
 * Easy drawings for kids — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easyDrawingsForKidsPack: ToilePack = {
    slug: 'easy-drawings-for-kids',
    category: 'coloring',
    name: 'Easy drawings for kids',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fish', label: 'Fish', subject: 'a six-step guide to drawing a fish in six numbered boxes, starting from an oval and adding a tail, fins, an eye and scales'},
        {key: 'house', label: 'House', subject: 'a six-step guide to drawing a house in six numbered boxes, starting from a square and adding a roof, door, windows and a chimney with smoke'},
        {key: 'owl', label: 'Owl', subject: 'a six-step guide to drawing an owl in six numbered boxes, starting from an egg shape and adding ears, big round eyes, a beak, wings and feet on a branch'},
    ],
    styles: [
        {key: 'marker', label: 'Marker', style: 'thick friendly marker lines and simple rounded shapes', palette: 'The new line of each step in red, the earlier lines black.', framing: 'Six equal boxes in two rows of three, each with a thin black border and the drawing centered in it.'},
        {key: 'pencil', label: 'Pencil', style: 'light pencil lines in a workbook style', palette: 'Earlier lines pale gray, the new line of each step dark black.', framing: 'Six equal boxes in two rows of three, each with a thin black border and the drawing centered in it.'},
        {key: 'dotted', label: 'Dotted guide', style: 'a bold black outline with dotted guide lines showing where the next line goes', palette: 'Black lines, the dotted guide gray.', framing: 'Six equal boxes in two rows of three, each with a thin black border and the drawing centered in it.'},
    ],
    directions: ['a cat', 'a car', 'a rocket'],
    directionHint: 'try: a cat',
    seo: {
        title: 'Easy Drawings for Kids: Fish, House, Owl in Steps',
        metaDescription: 'Printable six-step drawing guides for kids, a fish, a house and an owl, each step adding one line, in marker, pencil or dotted guides.',
        h1: 'Easy drawings for kids: fish, house, owl, step by step',
        tagline: 'Six numbered steps per subject, each step adding a single line.',
        intro: 'Easy drawings for kids are step-by-step guides a parent or teacher prints so a child can draw something alone. Each of these nine pages shows one subject, a fish, a house or an owl, built in six numbered steps, each adding a single line. Each comes as marker lines, light pencil, or a bold outline with dotted guides. Type another subject in the direction box.',
        useCases: [
            'A drawing activity at home',
            'A lesson in a classroom',
            'A rainy-day activity',
            'A step guide for a school club',
        ],
        faq: [
            {q: 'Why six steps?', a: 'Each subject is split into six numbered boxes, so the child adds one line at a time and sees the drawing grow.'},
            {q: 'How is the new line shown?', a: 'In the marker style the new line of each step is red and the earlier lines are black. In the pencil style the earlier lines are pale gray and the new line is dark.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type it in the direction box, such as a cat, a car or a rocket. A typed subject is a request, so the result may differ from the samples.'},
        ],
    },
}
